import {
  ActionPointModalButtonClicked,
  BookButtonClicked,
  BookingConfirmed,
  BusinessEvent,
  BusinessEventHandler, CheckoutPageViewed, ContactHostFormSent, ContactHostFormViewed,
  ExperiencePageViewed,
  PageView, ReadMoreClicked, SearchPageViewed
} from "../../businessLayer";
import {WlWindow} from "typings/globals/cordova";
import Cookies from "js-cookie";
import {AB_TEST_COOKIE_NAME} from "~/constants";
import stringToKebabCase from "utils/textHelpers/stringToKebabCase";

declare const window: WlWindow;

function addDefaultParameters(inputParams: object, evt: BusinessEvent) {
  const experimentEventData = {};
  evt.experiments?.forEach((exp) => {
    experimentEventData[`experiment_${exp.id}`] = exp.assignedVariant;
  });

  // Track special store front ab test
  experimentEventData['storefront_ab_test'] = Cookies.get(AB_TEST_COOKIE_NAME) || 'not_in_test';

  return {
    traffic_type: evt.traffic_type,
    account_is_host: evt.account_is_host,
    account_id: evt.account_id,
    application: 'ue',
    ...experimentEventData,
    ...inputParams,
  };
}

const plausibleHandler: BusinessEventHandler = (evt: BusinessEvent) => {
  if (!window || !window.plausible) {
    return;
  }

  if (evt.type === 'PageView') {
    const pageView = evt as PageView;
    const eventData = addDefaultParameters({
      page_title: pageView.title,
      page_location: pageView.location,
      page_path: pageView.path,
    }, pageView);

    return sendEvent(evt,'pageview', {
      props: {
        ...eventData
      }
    });
  }

  if (evt.type === 'SearchPageViewed') {
    const event = evt as SearchPageViewed;
    const eventData = addDefaultParameters({
      city: event.city,
      category: event.category,
    }, event);

    return sendEvent(evt,'page_view_search', {
      props: {
        ...eventData
      }
    });
  }

  if (evt.type === 'ExperiencePageViewed') {
    const event = evt as ExperiencePageViewed;
    const eventData = addDefaultParameters({
      experience: event.experience.id,
    }, event);

    return sendEvent(evt,'page_view_experience', {
      props: {
        ...eventData
      }
    });
  }

  if (evt.type === 'BookButtonClicked') {
    const event = evt as BookButtonClicked;
    const eventData = addDefaultParameters({
      experience_id: event?.experience?.id,
    }, event);

    return sendEvent(evt,'book_button_clicked', {
      props: {
        ...eventData
      }
    });
  }

  if (evt.type === 'ReadMoreClicked') {
    const event = evt as ReadMoreClicked;
    const eventData = addDefaultParameters({
      experience_id: event?.experience?.id,
    }, event);

    return sendEvent(evt,'read_more_clicked', {
      props: {
        ...eventData
      }
    });
  }

  if (evt.type === 'BookingConfirmed') {
    const event = evt as BookingConfirmed;
    const eventData = addDefaultParameters({
      trip_id: event.trip.id,
      amount: event.trip.required_payment,
      payment_id: event?.trip?.payment?.id,
    }, event);

    return sendEvent(evt,'checkout_completed', {
      props: {
        ...eventData
      }
    });
  }


  if (evt.type === 'CheckoutPageViewed') {
    const event = evt as CheckoutPageViewed;
    const eventData = addDefaultParameters({
      option: evt.type,
      experience_id: event?.experience?.id,
    }, event);

    return sendEvent(evt, 'page_view_checkout', {
      props: {
        ...eventData
      }
    });
  }

  if (evt.type === 'ActionPointModalButtonClicked') {
    const event = evt as ActionPointModalButtonClicked;
    const eventData = addDefaultParameters({
      option: evt.type,
      pathname: event?.pathname,
    }, event);

    return sendEvent(evt, 'action_point_modal_button_clicked', {
      props: {
        ...eventData
      }
    });
  }

  if (evt.type === 'ContactHostFormViewed') {
    const event = evt as ContactHostFormViewed;
    const hostLocation = event?.host?.hostLocations?.[0];
    const eventData = addDefaultParameters({
      experience_id: event?.experience?.id,
      host_id: event?.host?.id,
      source: event?.source,
      enabledForm: event?.enabledForm,
      country: stringToKebabCase(hostLocation?.country),
      city: stringToKebabCase(hostLocation?.area),
    }, event);

    return sendEvent(evt,'page_view_contact_host_form', {
      props: {
        ...eventData
      }
    });
  }

  if (evt.type === 'ContactHostFormSent') {
    const event = evt as ContactHostFormSent;
    const hostLocation = event?.host?.hostLocations?.[0];
    const eventData = addDefaultParameters({
      experience_id: event?.experience?.id,
      host_id: event?.host?.id,
      source: event?.source,
      enabledForm: event?.enabledForm,
      country: stringToKebabCase(hostLocation?.country),
      city: stringToKebabCase(hostLocation?.area),
      nrOfAdults: event?.nrOfAdults,
      nrOfChildren: event?.nrOfChildren,
      date: event?.date,
      time: event?.time,
    }, event);

    return sendEvent(evt,'contact_host_form_sent', {
      props: {
        ...eventData
      }
    });
  }

  console.warn('[PLAUSIBLE] received non-implemented event:', evt);
};

const sendEvent = (originalEvent: BusinessEvent, eventName: string, eventData: { props: {[key: string]: any } }) => {
  if (!window || !window.plausible) {
    return;
  }

  console.log('[PLAUSIBLE] sending event:', originalEvent.type, eventName, eventData);

  return window.plausible(eventName, eventData);
}

export {
  plausibleHandler
}
